import React, { useEffect, useMemo, useState } from "react";
import {
    ChevronRight,
    ChevronDown,
    Plus,
    Pencil,
    Save,
    X,
    Trash2,
    FolderPlus,
    Languages,
    RefreshCw,
} from "lucide-react";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

async function apiRequest(path, options = {}) {
    const res = await fetch(`${API_BASE}${path}`, {
        headers: {
            "Content-Type": "application/json",
            ...(options.headers || {}),
        },
        ...options,
    });

    const data = await res.json().catch(() => ({}));

    if (!res.ok) {
        throw new Error(data?.message || "Request failed");
    }

    return data;
}

function isPlainObject(value) {
    return value && typeof value === "object" && !Array.isArray(value);
}

function getNestedValue(obj, path) {
    if (!path) return obj;
    return path.split(".").reduce((acc, key) => (acc && acc[key] !== undefined ? acc[key] : undefined), obj);
}

function collectUnionKeys(...objects) {
    const set = new Set();
    objects.forEach((obj) => {
        if (isPlainObject(obj)) {
            Object.keys(obj).forEach((k) => set.add(k));
        }
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
}

function buildTree(enDoc, arDoc, heDoc, parentPath = "") {
    const keys = collectUnionKeys(enDoc, arDoc, heDoc);

    return keys.map((key) => {
        const path = parentPath ? `${parentPath}.${key}` : key;
        const enVal = enDoc?.[key];
        const arVal = arDoc?.[key];
        const heVal = heDoc?.[key];

        const isObjectNode = isPlainObject(enVal) || isPlainObject(arVal) || isPlainObject(heVal);

        if (isObjectNode) {
            return {
                key,
                path,
                type: "object",
                children: buildTree(
                    isPlainObject(enVal) ? enVal : {},
                    isPlainObject(arVal) ? arVal : {},
                    isPlainObject(heVal) ? heVal : {},
                    path
                ),
            };
        }

        return {
            key,
            path,
            type: "value",
            en: typeof enVal === "string" ? enVal : "",
            ar: typeof arVal === "string" ? arVal : "",
            he: typeof heVal === "string" ? heVal : "",
        };
    });
}

function flattenTree(nodes, expandedMap, depth = 0) {
    const out = [];

    for (const node of nodes) {
        out.push({ ...node, depth });

        if (node.type === "object" && expandedMap[node.path]) {
            out.push(...flattenTree(node.children || [], expandedMap, depth + 1));
        }
    }

    return out;
}

function collectAllObjectPaths(nodes, result = {}) {
    for (const node of nodes) {
        if (node.type === "object") {
            result[node.path] = true;
            collectAllObjectPaths(node.children || [], result);
        }
    }
    return result;
}

export default function AdminLanguage() {
    const [docs, setDocs] = useState({ en: {}, ar: {}, he: {} });
    const [loading, setLoading] = useState(true);
    const [savingPath, setSavingPath] = useState("");
    const [deletingPath, setDeletingPath] = useState("");
    const [error, setError] = useState("");

    const [expanded, setExpanded] = useState({});
    const [search, setSearch] = useState("");

    const [editingPath, setEditingPath] = useState("");
    const [editValues, setEditValues] = useState({ en: "", ar: "", he: "" });

    const [addModalOpen, setAddModalOpen] = useState(false);
    const [addParentPath, setAddParentPath] = useState("");
    const [addKey, setAddKey] = useState("");
    const [addType, setAddType] = useState("value"); // value | object
    const [addValues, setAddValues] = useState({ en: "", ar: "", he: "" });
    const [adding, setAdding] = useState(false);

    const [deleteConfirm, setDeleteConfirm] = useState({
        open: false,
        path: "",
        key: "",
    });

    const fetchTree = async () => {
        try {
            setLoading(true);
            setError("");
            const data = await apiRequest("/api/language/admin/tree");
            setDocs({
                en: data?.en || {},
                ar: data?.ar || {},
                he: data?.he || {},
            });
        } catch (err) {
            console.error(err);
            setError(err.message || "Failed to load translations.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTree();
    }, []);

    const tree = useMemo(() => {
        return buildTree(docs.en || {}, docs.ar || {}, docs.he || {});
    }, [docs]);

    useEffect(() => {
        setExpanded({});
    }, [tree]);

    const visibleRows = useMemo(() => {
        let rows = flattenTree(tree, expanded);

        const q = search.trim().toLowerCase();
        if (!q) return rows;

        return rows.filter((row) => {
            if (row.path.toLowerCase().includes(q)) return true;
            if (row.key.toLowerCase().includes(q)) return true;
            if (row.type === "value") {
                return [row.en, row.ar, row.he].some((v) => String(v || "").toLowerCase().includes(q));
            }
            return false;
        });
    }, [tree, expanded, search]);

    const toggleExpand = (path) => {
        setExpanded((prev) => ({
            ...prev,
            [path]: !prev[path],
        }));
    };

    const expandAll = () => {
        setExpanded(collectAllObjectPaths(tree, {}));
    };

    const collapseAll = () => {
        setExpanded({});
    };

    const startEdit = (row) => {
        setEditingPath(row.path);
        setEditValues({
            en: row.en || "",
            ar: row.ar || "",
            he: row.he || "",
        });
    };

    const cancelEdit = () => {
        setEditingPath("");
        setEditValues({ en: "", ar: "", he: "" });
    };

    const saveEdit = async (path) => {
        try {
            setSavingPath(path);
            await apiRequest("/api/language/admin/value", {
                method: "PATCH",
                body: JSON.stringify({
                    path,
                    en: editValues.en,
                    ar: editValues.ar,
                    he: editValues.he,
                }),
            });

            await fetchTree();
            cancelEdit();
        } catch (err) {
            console.error(err);
            alert(err.message || "Failed to save translation.");
        } finally {
            setSavingPath("");
        }
    };

    const openAddModal = (parentPath = "") => {
        setAddParentPath(parentPath);
        setAddKey("");
        setAddType("value");
        setAddValues({ en: "", ar: "", he: "" });
        setAddModalOpen(true);
    };

    const submitAdd = async () => {
        if (!addKey.trim()) {
            alert("Please enter a key.");
            return;
        }

        if (addType === "value" && (!addValues.en || !addValues.ar || !addValues.he)) {
            alert("Please fill all three translation values.");
            return;
        }

        try {
            setAdding(true);
            await apiRequest("/api/language/admin/key", {
                method: "POST",
                body: JSON.stringify({
                    parentPath: addParentPath,
                    key: addKey.trim(),
                    type: addType,
                    en: addValues.en,
                    ar: addValues.ar,
                    he: addValues.he,
                }),
            });

            await fetchTree();

            if (addParentPath) {
                setExpanded((prev) => ({ ...prev, [addParentPath]: true }));
            }

            setAddModalOpen(false);
        } catch (err) {
            console.error(err);
            alert(err.message || "Failed to add key.");
        } finally {
            setAdding(false);
        }
    };

    const requestDelete = (row) => {
        setDeleteConfirm({
            open: true,
            path: row.path,
            key: row.key,
        });
    };

    const confirmDelete = async () => {
        if (!deleteConfirm.path) return;

        try {
            setDeletingPath(deleteConfirm.path);
            await apiRequest("/api/language/admin/key", {
                method: "DELETE",
                body: JSON.stringify({
                    path: deleteConfirm.path,
                }),
            });

            await fetchTree();
            setDeleteConfirm({ open: false, path: "", key: "" });
        } catch (err) {
            console.error(err);
            alert(err.message || "Failed to delete key.");
        } finally {
            setDeletingPath("");
        }
    };

    const getParentLabel = () => {
        return addParentPath ? addParentPath : "root";
    };

    return (
        <div className="min-h-screen bg-gray-50 p-4 sm:p-6">
            <div className="max-w-7xl mx-auto space-y-4">
                <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                        <div>
                            <div className="flex items-center gap-2">
                                <Languages className="w-5 h-5 text-gray-700" />
                                <h1 className="text-xl font-bold text-gray-900">Manage Translations</h1>
                            </div>
                            <p className="text-sm text-gray-500 mt-1">
                                Edit nested translation keys for English, Arabic, and Hebrew.
                            </p>
                        </div>

                        <div className="flex flex-col sm:flex-row gap-2">
                            <button
                                onClick={fetchTree}
                                className="px-4 py-2 rounded-xl border border-gray-300 bg-white hover:bg-gray-50 flex items-center gap-2"
                            >
                                <RefreshCw className="w-4 h-4" />
                                Refresh
                            </button>

                            <button
                                onClick={expandAll}
                                className="px-4 py-2 rounded-xl border border-gray-300 bg-white hover:bg-gray-50"
                            >
                                Expand All
                            </button>

                            <button
                                onClick={collapseAll}
                                className="px-4 py-2 rounded-xl border border-gray-300 bg-white hover:bg-gray-50"
                            >
                                Collapse All
                            </button>

                            <button
                                onClick={() => openAddModal("")}
                                className="px-4 py-2 rounded-xl bg-blue-600 text-white hover:bg-blue-700 flex items-center gap-2"
                            >
                                <Plus className="w-4 h-4" />
                                Add Root Key
                            </button>
                        </div>
                    </div>

                    <div className="mt-4">
                        <input
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search by key path or value..."
                            className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>

                    {error ? (
                        <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-red-700">
                            {error}
                        </div>
                    ) : null}
                </div>

                <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
                    <div className="hidden lg:grid lg:grid-cols-12 gap-3 px-5 py-4 bg-gray-50 border-b border-gray-200 text-xs font-semibold uppercase tracking-wide text-gray-500">
                        <div className="lg:col-span-3">Key</div>
                        <div className="lg:col-span-2">English</div>
                        <div className="lg:col-span-2">Arabic</div>
                        <div className="lg:col-span-2">Hebrew</div>
                        <div className="lg:col-span-3 text-right">Actions</div>
                    </div>

                    {loading ? (
                        <div className="px-5 py-10 text-center text-gray-500">Loading translations...</div>
                    ) : visibleRows.length === 0 ? (
                        <div className="px-5 py-10 text-center text-gray-500">No translation keys found.</div>
                    ) : (
                        <div className="divide-y divide-gray-200">
                            {visibleRows.map((row) => {
                                const isEditing = editingPath === row.path;
                                const isSavingThis = savingPath === row.path;
                                const isDeletingThis = deletingPath === row.path;

                                return (
                                    <div key={row.path} className="px-4 sm:px-5 py-4">
                                        <div className="lg:hidden space-y-3">
                                            <div className="flex items-start justify-between gap-3">
                                                <div className="min-w-0 flex-1">
                                                    <div
                                                        className="flex items-center gap-2"
                                                        style={{ paddingInlineStart: `${row.depth * 18}px` }}
                                                    >
                                                        {row.type === "object" ? (
                                                            <button
                                                                type="button"
                                                                onClick={() => toggleExpand(row.path)}
                                                                className="p-1 rounded hover:bg-gray-100"
                                                            >
                                                                {expanded[row.path] ? (
                                                                    <ChevronDown className="w-4 h-4 text-gray-600" />
                                                                ) : (
                                                                    <ChevronRight className="w-4 h-4 text-gray-600" />
                                                                )}
                                                            </button>
                                                        ) : (
                                                            <span className="w-6" />
                                                        )}

                                                        <div className="min-w-0">
                                                            <div className="font-semibold text-gray-900 break-all">{row.key}</div>
                                                            <div className="text-xs text-gray-500 break-all">{row.path}</div>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="shrink-0">
                                                    <span
                                                        className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium border ${row.type === "object"
                                                                ? "bg-indigo-50 text-indigo-700 border-indigo-200"
                                                                : "bg-emerald-50 text-emerald-700 border-emerald-200"
                                                            }`}
                                                    >
                                                        {row.type}
                                                    </span>
                                                </div>
                                            </div>

                                            {row.type === "value" && (
                                                <div className="grid gap-2">
                                                    <input
                                                        disabled={!isEditing}
                                                        value={isEditing ? editValues.en : row.en}
                                                        onChange={(e) => setEditValues((prev) => ({ ...prev, en: e.target.value }))}
                                                        placeholder="English"
                                                        className="px-3 py-2 rounded-lg border border-gray-300 disabled:bg-gray-50"
                                                    />
                                                    <input
                                                        disabled={!isEditing}
                                                        value={isEditing ? editValues.ar : row.ar}
                                                        onChange={(e) => setEditValues((prev) => ({ ...prev, ar: e.target.value }))}
                                                        placeholder="Arabic"
                                                        className="px-3 py-2 rounded-lg border border-gray-300 disabled:bg-gray-50"
                                                    />
                                                    <input
                                                        disabled={!isEditing}
                                                        value={isEditing ? editValues.he : row.he}
                                                        onChange={(e) => setEditValues((prev) => ({ ...prev, he: e.target.value }))}
                                                        placeholder="Hebrew"
                                                        className="px-3 py-2 rounded-lg border border-gray-300 disabled:bg-gray-50"
                                                    />
                                                </div>
                                            )}

                                            <div className="flex flex-wrap gap-2 justify-end">
                                                {row.type === "object" ? (
                                                    <>
                                                        <button
                                                            onClick={() => openAddModal(row.path)}
                                                            className="px-3 py-2 rounded-lg border hover:bg-gray-50 flex items-center gap-2"
                                                        >
                                                            <FolderPlus className="w-4 h-4" />
                                                            Add Child
                                                        </button>
                                                        <button
                                                            onClick={() => requestDelete(row)}
                                                            disabled={isDeletingThis}
                                                            className="px-3 py-2 rounded-lg border text-red-600 hover:bg-red-50 flex items-center gap-2"
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                            Delete
                                                        </button>
                                                    </>
                                                ) : isEditing ? (
                                                    <>
                                                        <button
                                                            onClick={() => saveEdit(row.path)}
                                                            disabled={isSavingThis}
                                                            className="px-3 py-2 rounded-lg bg-green-600 text-white hover:bg-green-700 flex items-center gap-2 disabled:opacity-50"
                                                        >
                                                            <Save className="w-4 h-4" />
                                                            {isSavingThis ? "Saving..." : "Save"}
                                                        </button>
                                                        <button
                                                            onClick={cancelEdit}
                                                            className="px-3 py-2 rounded-lg border hover:bg-gray-50 flex items-center gap-2"
                                                        >
                                                            <X className="w-4 h-4" />
                                                            Cancel
                                                        </button>
                                                    </>
                                                ) : (
                                                    <>
                                                        <button
                                                            onClick={() => startEdit(row)}
                                                            className="px-3 py-2 rounded-lg border hover:bg-gray-50 flex items-center gap-2"
                                                        >
                                                            <Pencil className="w-4 h-4" />
                                                            Edit
                                                        </button>
                                                        <button
                                                            onClick={() => requestDelete(row)}
                                                            disabled={isDeletingThis}
                                                            className="px-3 py-2 rounded-lg border text-red-600 hover:bg-red-50 flex items-center gap-2"
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                            Delete
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        </div>

                                        <div className="hidden lg:grid lg:grid-cols-12 gap-3 items-start">
                                            <div className="lg:col-span-3 min-w-0">
                                                <div
                                                    className="flex items-center gap-2"
                                                    style={{ paddingInlineStart: `${row.depth * 18}px` }}
                                                >
                                                    {row.type === "object" ? (
                                                        <button
                                                            type="button"
                                                            onClick={() => toggleExpand(row.path)}
                                                            className="p-1 rounded hover:bg-gray-100"
                                                        >
                                                            {expanded[row.path] ? (
                                                                <ChevronDown className="w-4 h-4 text-gray-600" />
                                                            ) : (
                                                                <ChevronRight className="w-4 h-4 text-gray-600" />
                                                            )}
                                                        </button>
                                                    ) : (
                                                        <span className="w-6" />
                                                    )}

                                                    <div className="min-w-0">
                                                        <div className="font-semibold text-gray-900 break-all">{row.key}</div>
                                                        <div className="text-xs text-gray-500 break-all">{row.path}</div>
                                                    </div>
                                                </div>
                                            </div>

                                            {row.type === "value" ? (
                                                <>
                                                    <div className="lg:col-span-2">
                                                        <input
                                                            disabled={!isEditing}
                                                            value={isEditing ? editValues.en : row.en}
                                                            onChange={(e) => setEditValues((prev) => ({ ...prev, en: e.target.value }))}
                                                            className="w-full px-3 py-2 rounded-lg border border-gray-300 disabled:bg-gray-50"
                                                        />
                                                    </div>

                                                    <div className="lg:col-span-2">
                                                        <input
                                                            disabled={!isEditing}
                                                            value={isEditing ? editValues.ar : row.ar}
                                                            onChange={(e) => setEditValues((prev) => ({ ...prev, ar: e.target.value }))}
                                                            className="w-full px-3 py-2 rounded-lg border border-gray-300 disabled:bg-gray-50"
                                                        />
                                                    </div>

                                                    <div className="lg:col-span-2">
                                                        <input
                                                            disabled={!isEditing}
                                                            value={isEditing ? editValues.he : row.he}
                                                            onChange={(e) => setEditValues((prev) => ({ ...prev, he: e.target.value }))}
                                                            className="w-full px-3 py-2 rounded-lg border border-gray-300 disabled:bg-gray-50"
                                                        />
                                                    </div>
                                                </>
                                            ) : (
                                                <div className="lg:col-span-6 flex items-center">
                                                    <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-medium border bg-indigo-50 text-indigo-700 border-indigo-200">
                                                        object
                                                    </span>
                                                </div>
                                            )}

                                            <div className="lg:col-span-3">
                                                <div className="flex items-center justify-end gap-2">
                                                    {row.type === "object" ? (
                                                        <>
                                                            <button
                                                                onClick={() => openAddModal(row.path)}
                                                                className="px-3 py-2 rounded-lg border hover:bg-gray-50 flex items-center gap-2"
                                                            >
                                                                <FolderPlus className="w-4 h-4" />
                                                                Add Child
                                                            </button>
                                                            <button
                                                                onClick={() => requestDelete(row)}
                                                                disabled={isDeletingThis}
                                                                className="px-3 py-2 rounded-lg border text-red-600 hover:bg-red-50 flex items-center gap-2"
                                                            >
                                                                <Trash2 className="w-4 h-4" />
                                                                Delete
                                                            </button>
                                                        </>
                                                    ) : isEditing ? (
                                                        <>
                                                            <button
                                                                onClick={() => saveEdit(row.path)}
                                                                disabled={isSavingThis}
                                                                className="px-3 py-2 rounded-lg bg-green-600 text-white hover:bg-green-700 flex items-center gap-2 disabled:opacity-50"
                                                            >
                                                                <Save className="w-4 h-4" />
                                                                {isSavingThis ? "Saving..." : "Save"}
                                                            </button>
                                                            <button
                                                                onClick={cancelEdit}
                                                                className="px-3 py-2 rounded-lg border hover:bg-gray-50 flex items-center gap-2"
                                                            >
                                                                <X className="w-4 h-4" />
                                                                Cancel
                                                            </button>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <button
                                                                onClick={() => startEdit(row)}
                                                                className="px-3 py-2 rounded-lg border hover:bg-gray-50 flex items-center gap-2"
                                                            >
                                                                <Pencil className="w-4 h-4" />
                                                                Edit
                                                            </button>
                                                            <button
                                                                onClick={() => requestDelete(row)}
                                                                disabled={isDeletingThis}
                                                                className="px-3 py-2 rounded-lg border text-red-600 hover:bg-red-50 flex items-center gap-2"
                                                            >
                                                                <Trash2 className="w-4 h-4" />
                                                                Delete
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            {addModalOpen && (
                <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
                    <div className="w-full max-w-xl bg-white rounded-2xl shadow-xl border border-gray-200">
                        <div className="px-5 py-4 border-b border-gray-200">
                            <div className="text-lg font-bold text-gray-900">Add New Key</div>
                            <div className="text-sm text-gray-500 mt-1">
                                Parent path: <span className="font-medium">{getParentLabel()}</span>
                            </div>
                        </div>

                        <div className="p-5 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Key Name</label>
                                <input
                                    value={addKey}
                                    onChange={(e) => setAddKey(e.target.value)}
                                    placeholder="exampleKey"
                                    className="w-full px-4 py-2.5 rounded-xl border border-gray-300"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
                                <div className="flex gap-3">
                                    <label className="flex items-center gap-2 text-sm">
                                        <input
                                            type="radio"
                                            checked={addType === "value"}
                                            onChange={() => setAddType("value")}
                                        />
                                        Value
                                    </label>

                                    <label className="flex items-center gap-2 text-sm">
                                        <input
                                            type="radio"
                                            checked={addType === "object"}
                                            onChange={() => setAddType("object")}
                                        />
                                        Object
                                    </label>
                                </div>
                            </div>

                            {addType === "value" && (
                                <div className="grid gap-3">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">English</label>
                                        <input
                                            value={addValues.en}
                                            onChange={(e) => setAddValues((prev) => ({ ...prev, en: e.target.value }))}
                                            className="w-full px-4 py-2.5 rounded-xl border border-gray-300"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Arabic</label>
                                        <input
                                            value={addValues.ar}
                                            onChange={(e) => setAddValues((prev) => ({ ...prev, ar: e.target.value }))}
                                            className="w-full px-4 py-2.5 rounded-xl border border-gray-300"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Hebrew</label>
                                        <input
                                            value={addValues.he}
                                            onChange={(e) => setAddValues((prev) => ({ ...prev, he: e.target.value }))}
                                            className="w-full px-4 py-2.5 rounded-xl border border-gray-300"
                                        />
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="px-5 py-4 border-t border-gray-200 bg-gray-50 rounded-b-2xl flex items-center justify-end gap-3">
                            <button
                                onClick={() => setAddModalOpen(false)}
                                disabled={adding}
                                className="px-4 py-2.5 rounded-xl border border-gray-300 bg-white hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={submitAdd}
                                disabled={adding}
                                className="px-4 py-2.5 rounded-xl bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                            >
                                {adding ? "Adding..." : "Add Key"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {deleteConfirm.open && (
                <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
                    <div className="w-full max-w-md bg-white rounded-2xl shadow-xl border border-gray-200">
                        <div className="px-5 py-4 border-b border-gray-200">
                            <div className="text-lg font-bold text-gray-900">Delete key?</div>
                            <div className="text-sm text-gray-500 mt-1 break-all">
                                Are you sure you want to delete <span className="font-medium">{deleteConfirm.path}</span>?
                            </div>
                            <div className="text-sm text-red-600 mt-2">This action cannot be undone.</div>
                        </div>

                        <div className="px-5 py-4 bg-gray-50 rounded-b-2xl flex items-center justify-end gap-3">
                            <button
                                onClick={() => setDeleteConfirm({ open: false, path: "", key: "" })}
                                disabled={!!deletingPath}
                                className="px-4 py-2.5 rounded-xl border border-gray-300 bg-white hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmDelete}
                                disabled={!!deletingPath}
                                className="px-4 py-2.5 rounded-xl bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                            >
                                {deletingPath ? "Deleting..." : "Delete"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}